import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTickerAutocomplete } from '../hooks/useTickerAutocomplete'; // TICKER-SEARCH-AUTOCOMPLETE — addition, does not replace existing validate flow
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TICKER_COLORS = ['#6366f1', '#10b981', '#f59e0b'];

const BENCHMARKS = [
  { key: 'SPY',       label: 'S&P 500'   },
  { key: 'QQQ',       label: 'Nasdaq'    },
  { key: 'GLD',       label: 'Gold'      },
  { key: 'INFLATION', label: 'Inflation' },
  { key: 'BTC',       label: 'Bitcoin'   },
];

const DIVIDEND_MODES = [
  { key: 'reinvest', label: 'Reinvest',  desc: 'Compounds dividends back into shares (DRIP)' },
  { key: 'cash',     label: 'Hold cash', desc: 'Accumulates dividends as cash alongside shares' },
  { key: 'ignore',   label: 'Ignore',    desc: 'Excludes dividends from calculation' },
];

const MIN_YEAR = 2000;
const MAX_YEAR = new Date().getFullYear() - 1;

const S = {
  bg:      '#0f0f23',
  border:  '#1e1e3a',
  surface: '#1a1a2e',
  border2: '#2a2a4a',
  text:    '#e2e8f0',
  muted:   '#8b8ba7',
  subtle:  '#475569',
  green:   '#10b981',
  red:     '#ef4444',
  purple:  '#6366f1',
  amber:   '#f59e0b',
};

function fmtMoney(v) {
  if (v == null) return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(v) {
  if (v == null) return '—';
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
}
function fmtShares(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: S.muted, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: {fmtMoney(p.value)}
        </div>
      ))}
    </div>
  );
};

function DCAPanel({ dcaAmount, setDcaAmount, dcaFrequency, setDcaFrequency }) {
  const [enabled, setEnabled] = useState(false);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: enabled ? 12 : 0 }}>
        <div
          onClick={() => { setEnabled(!enabled); if (enabled) setDcaAmount('0'); }}
          style={{ width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'background 0.2s', background: enabled ? S.purple : S.border2, position: 'relative', flexShrink: 0 }}
        >
          <div style={{ position: 'absolute', top: 3, left: enabled ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
        </div>
        <span style={{ fontSize: 13, color: S.text }}>DCA — regular contributions</span>
      </div>
      {enabled && (
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>CONTRIBUTION AMOUNT</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: S.muted, fontSize: 14 }}>$</span>
              <input
                type="number" min="0" value={dcaAmount}
                onChange={e => setDcaAmount(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 8, padding: '10px 12px 10px 24px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>FREQUENCY</label>
            <select
              value={dcaFrequency} onChange={e => setDcaFrequency(e.target.value)}
              style={{ width: '100%', background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 8, padding: '10px 12px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function ExtraTickerInput({ value, onChange, onRemove, color, index }) {
  const [validating, setValidating] = useState(false);
  const [valid,      setValid]      = useState(null);
  const [name,       setName]       = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false); // TICKER-SEARCH-AUTOCOMPLETE
  const boxRef = useRef(null);
  const { suggestions } = useTickerAutocomplete(value); // reuses same debounce/limit defaults

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Store onChange in a ref so validate never needs onChange in its deps.
  // This breaks the re-render cycle: parent re-renders → new onChange ref →
  // validate recreated → useEffect fires → re-validates → flicker.
  const onChangeRef = React.useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const validate = useCallback(async (t) => {
    if (!t) return;
    setValidating(true);
    try {
      const res  = await fetch(`${API_URL}/api/calculator/validate/${t.toUpperCase()}`);
      const data = await res.json();
      setValid(data.valid);
      setName(data.valid ? data.name : '');
      onChangeRef.current(t.toUpperCase(), data.valid);
    } catch {
      setValid(false);
    } finally { setValidating(false); }
  }, []); // stable — no deps needed because onChange accessed via ref

  useEffect(() => {
    // Only re-validate when value actually changes (not on every parent render)
    if (!value || value.length < 1) { setValid(null); setName(''); return; }
    const t = setTimeout(() => validate(value), 600);
    return () => clearTimeout(t);
  }, [value, validate]);

  return (
    <div ref={boxRef} style={{ flex: 1, minWidth: 140, position: 'relative' }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
        TICKER {index + 2}
        <span onClick={onRemove} style={{ marginLeft: 8, color: S.red, cursor: 'pointer', fontSize: 10 }}>&#x2715; remove</span>
      </label>
      <input
        value={value}
        onChange={e => { onChange(e.target.value.toUpperCase(), null); setDropdownOpen(true); }}
        onFocus={() => { if (suggestions.length) setDropdownOpen(true); }}
        placeholder="e.g. MSFT"
        style={{
          width: '100%', boxSizing: 'border-box', background: S.surface, borderRadius: 8,
          padding: '10px 12px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
          border: `1px solid ${valid === false ? S.red : valid === true ? color : S.border2}`,
        }}
      />
      {/* TICKER-SEARCH-AUTOCOMPLETE — discovery only, existing validate flow above is unchanged */}
      {dropdownOpen && value.trim().length > 0 && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
          background: '#0a0a1a', border: `1px solid ${S.border2}`, borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
          {suggestions.map(s => (
            <button key={s.ticker} type="button"
              onClick={() => { onChange(s.ticker, null); setDropdownOpen(false); }}
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                padding: '8px 10px', background: 'none', border: 'none', borderBottom: `1px solid ${S.border}`,
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              <span style={{ fontSize: 12, fontWeight: 800, color, flex: '0 0 55px' }}>{s.ticker}</span>
              <span style={{ fontSize: 11, color: S.muted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
            </button>
          ))}
        </div>
      )}
      {validating && <div style={{ fontSize: 11, color: S.muted, marginTop: 4 }}>Validating&#8230;</div>}
      {valid === true  && <div style={{ fontSize: 11, color, marginTop: 4 }}>&#x2713; {name}</div>}
      {valid === false && <div style={{ fontSize: 11, color: S.red, marginTop: 4 }}>&#x2717; Not found</div>}
    </div>
  );
}

export default function CalculatorPage() {
  const [ticker,       setTicker]       = useState('AAPL');
  const [amount,       setAmount]       = useState('1000');
  const [fromYear,     setFromYear]     = useState('2010');
  const [benchmark,    setBenchmark]    = useState('SPY');
  const [dividendMode, setDividendMode] = useState('reinvest');
  const [dcaAmount,    setDcaAmount]    = useState('0');
  const [dcaFrequency, setDcaFrequency] = useState('monthly');
  const [inflationAdj, setInflationAdj] = useState(false);
  const [extraTickers, setExtraTickerList] = useState([]);

  const [validating,  setValidating]  = useState(false);
  const [tickerValid, setTickerValid] = useState(null);
  const [tickerName,  setTickerName]  = useState('');
  const [tickerError, setTickerError] = useState('');
  const [tickerDropdownOpen, setTickerDropdownOpen] = useState(false); // TICKER-SEARCH-AUTOCOMPLETE
  const tickerBoxRef = useRef(null);
  const { suggestions: tickerSuggestions } = useTickerAutocomplete(ticker); // reuses existing `ticker` state — no new debounce needed beyond the hook's own

  const [loading,       setLoading]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState('');
  const [showYearTable, setShowYearTable] = useState(false);

  const validateTicker = useCallback(async (t) => {
    if (!t) return;
    setValidating(true); setTickerValid(null); setTickerError('');
    try {
      const res  = await fetch(`${API_URL}/api/calculator/validate/${t.toUpperCase()}`);
      const data = await res.json();
      setTickerValid(data.valid);
      setTickerName(data.valid ? data.name : '');
      setTickerError(data.valid ? '' : data.message || 'Ticker not found');
    } catch { setTickerError('Could not validate ticker'); }
    finally { setValidating(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (ticker) validateTicker(ticker); }, 600);
    return () => clearTimeout(t);
  }, [ticker, validateTicker]);

  useEffect(() => {
    const handler = (e) => { if (tickerBoxRef.current && !tickerBoxRef.current.contains(e.target)) setTickerDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allValid = tickerValid && extraTickers.every(et => et.valid !== false);

  const handleCalculate = async () => {
    if (!allValid) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const from     = `${fromYear}-01-01`;
      const allT     = [ticker.toUpperCase(), ...extraTickers.filter(et => et.valid).map(et => et.ticker)];
      const tParam   = allT.length > 1 ? `tickers=${allT.join(',')}` : `ticker=${ticker.toUpperCase()}`;
      const url = `${API_URL}/api/calculator/calculate?${tParam}&amount=${amount}&from=${from}&dividendMode=${dividendMode}&benchmark=${benchmark}&dcaAmount=${dcaAmount}&dcaFrequency=${dcaFrequency}&inflationAdjust=${inflationAdj}`;
      const res  = await fetch(url);
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'error');
      setResult(data);
    } catch (e) {
      const msg = e.message || '';
      const noData = msg.toLowerCase().includes('no') && msg.toLowerCase().includes('data');
      setError(noData
        ? `No historical data available for ${ticker} from ${fromYear}. Try a later start year.`
        : 'Data temporarily unavailable — please try again in a moment.'
      );
    } finally { setLoading(false); }
  };

  const { chartData, splitEvents, isMulti } = useMemo(() => {
    if (!result) return { chartData: [], splitEvents: [], isMulti: false };
    const multi = result.mode === 'comparison';
    const map   = {};
    if (multi) {
      (result.tickers || []).forEach(td => {
        (td.series || []).forEach(({ date, value }) => {
          if (!map[date]) map[date] = { date };
          map[date][td.ticker] = value;
        });
      });
    } else {
      (result.series || []).forEach(({ date, value }) => { map[date] = { date, portfolio: value }; });
    }
    (result.bmSeries || []).forEach(({ date, value }) => {
      if (!map[date]) map[date] = { date };
      map[date].benchmark = value;
    });
    return {
      chartData:   Object.values(map).sort((a, b) => a.date.localeCompare(b.date)),
      splitEvents: result.splitEvents || [],
      isMulti:     multi,
    };
  }, [result]);

  const s     = result?.summary;
  const isWin = s?.beatsBenchmark;

  const shareUrl = result
    ? `${window.location.origin}/calculator?ticker=${result.ticker || ticker}&amount=${amount}&from=${fromYear}-01-01&benchmark=${benchmark}&dividendMode=${dividendMode}${inflationAdj ? '&inflationAdjust=true' : ''}`
    : null;

  return (
    <div style={{ padding: '32px 16px', color: S.text }}>

      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#f8fafc', margin: '0 0 8px' }}>
          Historical Performance Calculator
        </h1>
        <p style={{ color: S.muted, fontSize: 14, margin: 0 }}>
          What if you invested in a stock years ago? See split-adjusted, dividend-reinvested returns.
        </p>
      </div>

      {/* ── Input card ── */}
      <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>

        {/* Tickers + Amount + Year + Benchmark row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          <div ref={tickerBoxRef} style={{ flex: 1, minWidth: 140, position: 'relative' }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              {extraTickers.length > 0 ? 'TICKER 1' : 'TICKER'}
            </label>
            <input
              value={ticker}
              onChange={e => { setTicker(e.target.value.toUpperCase()); setTickerDropdownOpen(true); }}
              onFocus={() => { if (tickerSuggestions.length) setTickerDropdownOpen(true); }}
              placeholder="e.g. AAPL"
              style={{
                width: '100%', boxSizing: 'border-box', background: S.surface, borderRadius: 8,
                padding: '10px 12px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                border: `1px solid ${tickerValid === false ? S.red : tickerValid === true ? TICKER_COLORS[0] : S.border2}`,
              }}
            />
            {/* TICKER-SEARCH-AUTOCOMPLETE — discovery only. Selecting a candidate just
                calls setTicker(), which re-triggers the SAME existing debounced
                validateTicker effect above — zero duplication of validation logic. */}
            {tickerDropdownOpen && ticker.trim().length > 0 && tickerSuggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
                background: '#0a0a1a', border: `1px solid ${S.border2}`, borderRadius: 8, maxHeight: 240, overflowY: 'auto' }}>
                {tickerSuggestions.map(s => (
                  <button key={s.ticker} type="button"
                    onClick={() => { setTicker(s.ticker); setTickerDropdownOpen(false); }}
                    style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                      padding: '8px 10px', background: 'none', border: 'none', borderBottom: `1px solid ${S.border}`,
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: TICKER_COLORS[0], flex: '0 0 55px' }}>{s.ticker}</span>
                    <span style={{ fontSize: 11, color: S.muted, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  </button>
                ))}
              </div>
            )}
            {validating  && <div style={{ fontSize: 11, color: S.muted,         marginTop: 4 }}>Validating&#8230;</div>}
            {tickerValid === true  && <div style={{ fontSize: 11, color: TICKER_COLORS[0], marginTop: 4 }}>&#x2713; {tickerName}</div>}
            {tickerValid === false && <div style={{ fontSize: 11, color: S.red,          marginTop: 4 }}>&#x2717; {tickerError}</div>}
          </div>

          {extraTickers.map((et, i) => (
            <ExtraTickerInput key={i} index={i} value={et.ticker} color={TICKER_COLORS[i + 1]}
              onChange={(t, v) => {
                const u = [...extraTickers]; u[i] = { ticker: t, valid: v }; setExtraTickerList(u);
              }}
              onRemove={() => setExtraTickerList(extraTickers.filter((_, idx) => idx !== i))}
            />
          ))}

          {extraTickers.length < 2 && (
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <button
                onClick={() => setExtraTickerList([...extraTickers, { ticker: '', valid: null }])}
                style={{ padding: '10px 14px', background: 'transparent', border: `1px dashed ${S.border2}`, borderRadius: 8, color: S.muted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              >+ Compare</button>
            </div>
          )}

          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>INITIAL INVESTMENT</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: S.muted, fontSize: 14 }}>$</span>
              <input
                type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 8, padding: '10px 12px 10px 24px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
              />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>START YEAR</label>
            <select value={fromYear} onChange={e => setFromYear(e.target.value)}
              style={{ width: '100%', background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 8, padding: '10px 12px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}
            >
              {Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => MAX_YEAR - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>COMPARE VS</label>
            <select value={benchmark} onChange={e => setBenchmark(e.target.value)}
              style={{ width: '100%', background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 8, padding: '10px 12px', color: S.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}
            >
              {BENCHMARKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
        </div>

        {/* C3: Dividend treatment */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.muted, letterSpacing: '0.06em', marginBottom: 10 }}>DIVIDEND TREATMENT</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {DIVIDEND_MODES.map(m => (
              <button key={m.key} onClick={() => setDividendMode(m.key)}
                style={{
                  padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                  border: `1px solid ${dividendMode === m.key ? S.purple : S.border2}`,
                  background: dividendMode === m.key ? `${S.purple}22` : 'transparent',
                  color: dividendMode === m.key ? S.purple : S.muted,
                  fontWeight: dividendMode === m.key ? 700 : 400,
                }}
                title={m.desc}
              >{m.label}</button>
            ))}
            <span style={{ fontSize: 11, color: S.muted }}>
              {DIVIDEND_MODES.find(m => m.key === dividendMode)?.desc}
            </span>
          </div>
        </div>

        {/* C2: DCA */}
        <DCAPanel dcaAmount={dcaAmount} setDcaAmount={setDcaAmount} dcaFrequency={dcaFrequency} setDcaFrequency={setDcaFrequency} />

        {/* C5: Inflation toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <div
            onClick={() => setInflationAdj(!inflationAdj)}
            style={{ width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'background 0.2s', background: inflationAdj ? S.amber : S.border2, position: 'relative', flexShrink: 0 }}
          >
            <div style={{ position: 'absolute', top: 3, left: inflationAdj ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
          <span style={{ fontSize: 13, color: S.text }}>Inflation-adjusted (real returns)</span>
          <span style={{ fontSize: 11, color: S.muted }}>&#8212; CPI-deflated purchasing power</span>
        </div>

        <button
          onClick={handleCalculate} disabled={loading || !allValid}
          style={{
            padding: '12px 32px', borderRadius: 8, border: 'none', fontFamily: 'inherit',
            cursor: allValid ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 15,
            background: allValid ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : S.border2,
            color: allValid ? '#fff' : S.muted, transition: 'opacity 0.15s', opacity: loading ? 0.7 : 1,
          }}
        >{loading ? 'Calculating&#8230;' : 'Calculate'}</button>

        {/* C0: Generic error — no vendor names ever */}
        {error && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: S.red, fontSize: 13 }}>
            {error}
          </div>
        )}
      </div>

      {/* ── Results: single ticker ── */}
      {result && !isMulti && s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Initial Investment', value: fmtMoney(s.startValue),        sign: false },
              { label: 'Final Value',         value: fmtMoney(s.endValue),          sign: false },
              { label: 'Total Return',         value: fmtPct(s.totalReturn),         sign: true  },
              { label: 'CAGR',                 value: fmtPct(s.cagr),               sign: true  },
              ...(s.bmReturn != null ? [{
                label: `vs ${result.benchmark.label}`,
                value: fmtPct(s.totalReturn - s.bmReturn),
                sign: true,
                sub:  isWin ? '&#9650; Outperformed' : '&#9660; Underperformed',
              }] : []),
              ...(result.totalInvested > parseFloat(amount) + 1 ? [{
                label: 'Total Invested (DCA)',
                value: fmtMoney(result.totalInvested), sign: false,
              }] : []),
            ].map(({ label, value, sign, sub }) => {
              const num = parseFloat(value);
              const col = !sign ? S.text : num >= 0 ? S.green : S.red;
              return (
                <div key={label} style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: '16px 20px' }}>
                  <div style={{ fontSize: 11, color: S.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: col }}>{value}</div>
                  {sub && <div style={{ fontSize: 11, color: col, marginTop: 4 }} dangerouslySetInnerHTML={{ __html: sub }} />}
                </div>
              );
            })}
          </div>

          {result.inflationAdjusted && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: 13, color: S.amber }}>
              &#128202; Showing inflation-adjusted returns (real dollars). Nominal returns are higher.
            </div>
          )}
          {!result.benchmark.available && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: 13, color: S.amber }}>
              &#9888;&#65039; {result.benchmark.label} comparison unavailable for this period &#8212; showing portfolio only.
            </div>
          )}
        </>
      )}

      {/* ── Results: multi-ticker comparison (C6) ── */}
      {result && isMulti && (result.tickers || []).length > 0 && (
        <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: S.text, marginBottom: 16 }}>Comparison Results</div>

          {/* Metric rows — one row per metric, one column per ticker */}
          {(() => {
            const tickers = result.tickers || [];
            // Find winner index (highest totalReturn) for each metric
            const winnerReturn = tickers.reduce((best, td, i) => td.summary.totalReturn > tickers[best].summary.totalReturn ? i : best, 0);
            const winnerCAGR   = tickers.reduce((best, td, i) => td.summary.cagr       > tickers[best].summary.cagr       ? i : best, 0);
            const winnerFinal  = tickers.reduce((best, td, i) => td.summary.endValue   > tickers[best].summary.endValue   ? i : best, 0);

            const rows = [
              {
                label: 'Final Value',
                values: tickers.map(td => fmtMoney(td.summary.endValue)),
                colors: tickers.map((_, i) => i === winnerFinal ? S.text : S.muted),
                winner: winnerFinal,
                sign: false,
              },
              {
                label: 'Total Return',
                values: tickers.map(td => fmtPct(td.summary.totalReturn)),
                colors: tickers.map(td => td.summary.totalReturn >= 0 ? S.green : S.red),
                winner: winnerReturn,
                sign: true,
              },
              {
                label: 'CAGR (annualised)',
                values: tickers.map(td => fmtPct(td.summary.cagr)),
                colors: tickers.map(td => td.summary.cagr >= 0 ? S.green : S.red),
                winner: winnerCAGR,
                sign: true,
              },
              ...(tickers[0]?.summary?.bmReturn != null ? [{
                label: `vs ${result.benchmark?.label || 'Benchmark'}`,
                values: tickers.map(td => fmtPct(td.summary.totalReturn - td.summary.bmReturn)),
                colors: tickers.map(td => (td.summary.totalReturn - td.summary.bmReturn) >= 0 ? S.green : S.red),
                winner: tickers.reduce((best, td, i) =>
                  (td.summary.totalReturn - td.summary.bmReturn) > (tickers[best].summary.totalReturn - tickers[best].summary.bmReturn) ? i : best, 0),
                sign: true,
              }] : []),
            ];

            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 12px', color: S.muted, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', borderBottom: `1px solid ${S.border}`, width: 160 }}>METRIC</th>
                      {tickers.map((td, i) => (
                        <th key={td.ticker} style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 800, fontSize: 14, borderBottom: `1px solid ${S.border}`, color: TICKER_COLORS[i] }}>
                          {td.ticker}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.label} style={{ borderBottom: `1px solid ${S.border}22` }}>
                        <td style={{ padding: '10px 12px', color: S.muted, fontSize: 12, fontWeight: 600, letterSpacing: '0.03em' }}>{row.label}</td>
                        {tickers.map((td, i) => (
                          <td key={td.ticker} style={{ padding: '10px 12px', textAlign: 'right', fontWeight: i === row.winner ? 800 : 400, color: row.colors[i], fontSize: i === row.winner ? 14 : 13 }}>
                            {row.values[i]}
                            {i === row.winner && <span style={{ marginLeft: 6, fontSize: 11 }}>&#127942;</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Chart ── */}
      {result && chartData.length > 0 && (
        <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: S.text, marginBottom: 4 }}>
            Growth of {fmtMoney(parseFloat(amount))}
          </div>
          {result.inflationAdjusted && (
            <div style={{ fontSize: 11, color: S.amber, marginBottom: 12 }}>Inflation-adjusted values</div>
          )}
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  {isMulti
                    ? (result.tickers || []).map((td, i) => (
                        <linearGradient key={td.ticker} id={`grad${td.ticker}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={TICKER_COLORS[i]} stopOpacity={0.25} />
                          <stop offset="95%" stopColor={TICKER_COLORS[i]} stopOpacity={0}    />
                        </linearGradient>
                      ))
                    : <>
                        <linearGradient id="gradP" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}   />
                        </linearGradient>
                        <linearGradient id="gradB" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}   />
                        </linearGradient>
                      </>
                  }
                </defs>
                <XAxis dataKey="date" tick={{ fill: S.muted, fontSize: 11 }} axisLine={false} tickLine={false}
                  tickFormatter={d => d.slice(0, 7)} interval={Math.floor(chartData.length / 6)} />
                <YAxis tick={{ fill: S.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={70}
                  tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: S.muted, paddingTop: 8 }} />

                {/* C4: Split annotations */}
                {splitEvents.map(ev => (
                  <ReferenceLine key={ev.date} x={ev.date} stroke="#6366f188" strokeDasharray="3 3"
                    label={{ value: `${ev.ratio}:1 split`, position: 'insideTopLeft', fill: S.muted, fontSize: 10 }}
                  />
                ))}

                {isMulti
                  ? (result.tickers || []).map((td, i) => (
                      <Area key={td.ticker} type="monotone" dataKey={td.ticker} name={td.ticker}
                        stroke={TICKER_COLORS[i]} strokeWidth={2} fill={`url(#grad${td.ticker})`} dot={false} />
                    ))
                  : <Area type="monotone" dataKey="portfolio" name={result.ticker}
                      stroke="#6366f1" strokeWidth={2} fill="url(#gradP)" dot={false} />
                }
                {result.benchmark?.available && (
                  <Area type="monotone" dataKey="benchmark" name={result.benchmark.label}
                    stroke="#f59e0b" strokeWidth={2} fill="url(#gradB)" dot={false} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* C0: Source attribution — no vendor name */}
          <div style={{ fontSize: 11, color: S.subtle, textAlign: 'right', marginTop: 8 }}>
            Monthly split-adjusted historical data &#xB7; Licensed data providers
          </div>
        </div>
      )}

      {/* C1: Year-by-year breakdown */}
      {result && !isMulti && result.yearlyBreakdown?.length > 0 && (
        <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showYearTable ? 16 : 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: S.text }}>Year-by-Year Breakdown</div>
            <button
              onClick={() => setShowYearTable(!showYearTable)}
              style={{ background: 'transparent', border: `1px solid ${S.border2}`, borderRadius: 6, color: S.muted, fontSize: 12, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}
            >{showYearTable ? 'Hide' : 'Show table'}</button>
          </div>
          {showYearTable && (
            <div style={{ overflowX: 'auto', position: 'relative' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Year', 'Shares held', 'Dividends', 'Contributions', 'Portfolio value', 'Year change'].map(h => (
                      <th key={h} style={{ textAlign: 'right', padding: '8px 12px', color: S.muted, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', borderBottom: `1px solid ${S.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.yearlyBreakdown.map((row, i) => {
                    const prev   = result.yearlyBreakdown[i - 1];
                    const change = prev ? ((row.portfolioValue - prev.portfolioValue) / prev.portfolioValue) * 100 : null;
                    return (
                      <tr key={row.year} style={{ borderBottom: `1px solid ${S.border}22` }}>
                        <td style={{ padding: '8px 12px', color: S.text, fontWeight: 700, textAlign: 'right' }}>{row.year}</td>
                        <td style={{ padding: '8px 12px', color: S.muted, textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{fmtShares(row.sharesHeld)}</td>
                        <td style={{ padding: '8px 12px', color: row.dividendsReceived > 0 ? S.green : S.muted, textAlign: 'right' }}>{fmtMoney(row.dividendsReceived)}</td>
                        <td style={{ padding: '8px 12px', color: row.contributions > 0 ? S.purple : S.muted, textAlign: 'right' }}>{fmtMoney(row.contributions)}</td>
                        <td style={{ padding: '8px 12px', color: S.text, fontWeight: 600, textAlign: 'right' }}>{fmtMoney(row.portfolioValue)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: change == null ? S.muted : change >= 0 ? S.green : S.red }}>
                          {change == null ? '&#8212;' : fmtPct(change)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

            </div>
          )}
        </div>
      )}

      {/* Share */}
      {result && shareUrl && (
        <div style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: S.muted, flex: 1, minWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>&#128279; {shareUrl}</span>
          <button
            onClick={() => navigator.clipboard.writeText(shareUrl)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${S.purple}`, background: 'transparent', color: S.purple, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >Copy Link</button>
        </div>
      )}

      {result && (
        <div style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
          <strong style={{ color: '#64748b' }}>Past performance does not guarantee future results.</strong> This calculator is for informational and educational purposes only. It does not constitute financial advice or a recommendation to buy or sell any security. Returns shown are hypothetical and based on historical adjusted prices. Actual returns will vary. Always consult a qualified financial advisor before making investment decisions.
        </div>
      )}
    </div>
  );
}
